/**
 * useFirmClientAct — the firm-console compliance-write routing helper (AC4).
 *
 * A firm operator completing a compliance action (open an EDD case, …) for a
 * CLIENT trust must execute the write in that client's tenant, authorised by the
 * firm's grant. This hook mints a per-client act-as token (POST /v1/firm/clients/
 * {clientTenantId}/act-as) that the write then rides via the api() `actAsToken`
 * option — so the console's global posture is untouched (the /admin layout
 * clears the global firm-act context on entry) and exactly one write targets the
 * picked trust's tenant.
 *
 * Security: the mint is grant-gated server-side. A `manage` grant yields an
 * 'admin' (writer) token; a `read` grant yields a write-denied 'firm-reader'
 * token; no grant → 404. So a read-only or ungranted firm can NEVER write into a
 * client — the worst case is a clean 403/404, never a silent wrong-tenant write.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useActAs } from './api/firm-clients';
import type { ActAsResponse } from './api/firm-clients';
import { mutateReplaySafe } from './api/team';

export interface FirmClientActApi {
  /** Mint a per-client act-as token for a compliance write. Resolves with the
   *  token + the grant accessLevel; rejects (ApiError) on a read-only / absent
   *  grant so the caller can surface an honest message instead of writing. */
  mintForClient: (clientTenantId: string) => Promise<ActAsResponse>;
  /** True while a mint is in flight (drives the submit button's busy state). */
  isMinting: boolean;
}

export function useFirmClientAct(): FirmClientActApi {
  const actAs = useActAs();
  const qc = useQueryClient();
  return {
    // 2026-08-22: the act-as mint is step-up gated server-side now. A bare
    // `mutateAsync` rejects on the STEP_UP_REQUIRED 403 and the interceptor's
    // replay never resolves this promise — the first compliance write of a
    // session would pass MFA and then go nowhere. `mutateReplaySafe` resolves
    // with the terminal outcome across the replay (same fix as the Client
    // Book's mintActAsAndEnter).
    mintForClient: (clientTenantId: string) => mutateReplaySafe(qc, actAs, { clientTenantId }),
    isMinting: actAs.isPending,
  };
}
